import Vue from 'vue'
import gql from '@my/graphql'
import gqlOrder from '@/graphql/order'
import modals from '@my/store/modals'
import {
  mapOrderDataNoveltyListItem,
  mapOrderProductChangeListItem,
  orderHasRecentNoveltyData,
  orderHasRecentProductChange,
  buildRemitPatch,
  needsNewDeliveryForDataNovelty
} from '@/helpers/orderNovelty'
import gqlCoordinadora from '@/graphql/coordinadora'

function formatRemitError (error) {
  if (!error) return 'no hubo respuesta del servidor'
  if (typeof error === 'string') return error
  if (error.message) return error.message
  return String(error)
}

function applyRemitFields (target, dataUpdated) {
  if (!target || !dataUpdated) return

  const patch = buildRemitPatch(dataUpdated)

  Object.keys(patch).forEach(key => {
    const value = (key === 'remitDataGenerated' || key === 'dataLocked')
      ? !!patch[key]
      : patch[key]

    Vue.set(target, key, value)
  })
}

const NOVELTY_SESSION_KEY = 'orderNoveltyQuotes'

function readNoveltySession () {
  try {
    return JSON.parse(sessionStorage.getItem(NOVELTY_SESSION_KEY)) || {}
  } catch (_) {
    return {}
  }
}

function writeNoveltySession (session) {
  sessionStorage.setItem(NOVELTY_SESSION_KEY, JSON.stringify(session))
}

function sessionQuoteFromOrder (order) {
  if (!order || !order.order) return null

  return {
    coordinadoraCost: order.coordinadoraCost,
    declared: order.declared,
    package: order.package,
    packageDimensions: order.packageDimensions
  }
}

function persistNoveltyQuote (order) {
  if (!order || !order.order) return

  const session = readNoveltySession()
  session[order.order] = sessionQuoteFromOrder(order)
  writeNoveltySession(session)
}

function preserveDataNoveltySession (existing) {
  if (!existing) return {}

  const stored = existing.order ? readNoveltySession()[existing.order] : null

  return {
    coordinadoraCost: existing.coordinadoraCost !== undefined
      ? existing.coordinadoraCost
      : stored && stored.coordinadoraCost,
    declared: existing.declared !== undefined
      ? existing.declared
      : stored && stored.declared,
    package: existing.package !== undefined
      ? existing.package
      : stored && stored.package,
    packageDimensions: existing.packageDimensions !== undefined
      ? existing.packageDimensions
      : stored && stored.packageDimensions
  }
}

function mergeDataNoveltyOrder (existing, incoming) {
  const preserve = preserveDataNoveltySession(existing)

  return mapOrderDataNoveltyListItem({
    ...existing,
    ...preserve,
    ...(incoming || {})
  })
}

export default {
  namespaced: true,

  state: {
    dataCount: 0,
    productCount: 0,
    dataLoading: false,
    productLoading: false,
    dataOrders: [],
    productOrders: [],
    dataSelectedOrders: {},
    activeTab: 'data',
    productDetailOrder: null,
    productDetailLoading: false
  },

  mutations: {
    setDataNoveltyCount (state, count) {
      state.dataCount = count || 0
    },

    setProductChangeCount (state, count) {
      state.productCount = count || 0
    },

    setDataNoveltyLoading (state, loading) {
      state.dataLoading = !!loading
    },

    setProductChangeLoading (state, loading) {
      state.productLoading = !!loading
    },

    setDataNoveltyOrders (state, orders) {
      const session = readNoveltySession()

      state.dataOrders = (orders || []).map(order => {
        const stored = session[order.order] || {}
        return mapOrderDataNoveltyListItem({
          ...order,
          ...stored
        })
      })
      state.dataSelectedOrders = {}
    },

    setProductChangeOrders (state, orders) {
      state.productOrders = orders || []
    },

    setNoveltyActiveTab (state, tab) {
      state.activeTab = tab === 'product' ? 'product' : 'data'
    },

    setNoveltySelected (state, { order, value }) {
      Vue.set(state.dataSelectedOrders, order, value)
    },

    setNoveltyAllSelected (state, { orderIds, value }) {
      orderIds.forEach(orderId => {
        Vue.set(state.dataSelectedOrders, orderId, value)
      })
    },

    clearNoveltySelection (state) {
      state.dataSelectedOrders = {}
    },

    setProductDetailOrder (state, order) {
      state.productDetailOrder = order
    },

    setProductDetailLoading (state, loading) {
      state.productDetailLoading = !!loading
    },

    syncDataNoveltyFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.order) return

      const index = state.dataOrders.findIndex(item => item.order === dataUpdated.order)
      const qualifies = orderHasRecentNoveltyData(dataUpdated)

      if (qualifies) {
        const mapped = mergeDataNoveltyOrder(
          index >= 0 ? state.dataOrders[index] : {},
          dataUpdated
        )

        if (index >= 0) {
          Vue.set(state.dataOrders, index, mapped)
        } else {
          state.dataOrders.unshift(mapped)
        }

        persistNoveltyQuote(mapped)
        return
      }

      if (index >= 0) {
        state.dataOrders.splice(index, 1)
      }
    },

    syncProductChangeFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.order) return

      const index = state.productOrders.findIndex(item => item.order === dataUpdated.order)
      const qualifies = orderHasRecentProductChange(dataUpdated)

      if (qualifies) {
        const mapped = mapOrderProductChangeListItem({
          ...(index >= 0 ? state.productOrders[index] : {}),
          ...dataUpdated
        })

        if (index >= 0) {
          Vue.set(state.productOrders, index, mapped)
        } else {
          state.productOrders.unshift(mapped)
        }
      } else if (index >= 0) {
        state.productOrders.splice(index, 1)
      }

      if (state.productDetailOrder && state.productDetailOrder.order === dataUpdated.order) {
        if (qualifies) {
          state.productDetailOrder = {
            ...state.productDetailOrder,
            ...dataUpdated
          }
        } else {
          state.productDetailOrder = null
        }
      }
    },

    patchDataNoveltyFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.order) return

      const index = state.dataOrders.findIndex(item => item.order === dataUpdated.order)
      if (index < 0) return

      const mapped = mergeDataNoveltyOrder(
        state.dataOrders[index],
        dataUpdated
      )
      Vue.set(state.dataOrders, index, mapped)
      persistNoveltyQuote(mapped)
    },

    patchDataNoveltyFields (state, { order, patch }) {
      if (!order || !patch) return

      const index = state.dataOrders.findIndex(item => item.order === order)
      if (index < 0) return

      const mapped = mergeDataNoveltyOrder(
        state.dataOrders[index],
        patch
      )
      Vue.set(state.dataOrders, index, mapped)
      persistNoveltyQuote(mapped)
    },

    patchDataNoveltyRemitFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.order) return

      const found = state.dataOrders.find(item => item.order === dataUpdated.order)
      if (found) applyRemitFields(found, dataUpdated)
    },

    patchProductChangeRemitFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.order) return

      const found = state.productOrders.find(item => item.order === dataUpdated.order)
      if (found) applyRemitFields(found, dataUpdated)

      if (state.productDetailOrder && state.productDetailOrder.order === dataUpdated.order) {
        applyRemitFields(state.productDetailOrder, dataUpdated)
      }
    }
  },

  actions: {
    async fetchDataNoveltyCount ({ commit }) {
      try {
        const response = await gql.query(gqlOrder.GET_DATA_NOVELTY_COUNT, {}, false, true, true)

        if (response && response.data) {
          commit('setDataNoveltyCount', response.data.getOrderDataNoveltyCount)
        }
      } catch (_) {}
    },

    async fetchProductChangeCount ({ commit }) {
      try {
        const response = await gql.query(gqlOrder.GET_PRODUCT_CHANGE_COUNT, {}, false, true, true)

        if (response && response.data) {
          commit('setProductChangeCount', response.data.getOrderProductChangeCount)
        }
      } catch (_) {}
    },

    async fetchDataNovelties ({ commit }) {
      commit('setDataNoveltyLoading', true)

      try {
        const response = await gql.query(gqlOrder.GET_DATA_NOVELTIES, {}, false, true, true)

        if (response && response.data) {
          commit('setDataNoveltyOrders', response.data.getOrderDataNovelties || [])
        }
      } finally {
        commit('setDataNoveltyLoading', false)
      }
    },

    async refreshDataNoveltyOrder ({ commit, state }, orderId) {
      if (!orderId) return { success: false }

      const response = await gql.query(gqlOrder.GET_BY_ORDER_ADMIN, { order: orderId }, false, true, true)
      const found = response && response.data && response.data.getByOrderAdmin

      if (!found) return { success: false }

      commit('patchDataNoveltyFields', {
        order: orderId,
        patch: found
      })

      return { success: true, order: state.dataOrders.find(item => item.order === orderId) }
    },

    async quoteDataNoveltyOrder ({ commit }, { order, packageDimensions = null }) {
      if (!order) return { success: false }

      const payload = packageDimensions && order.package === 'otro' ? packageDimensions : null

      if (order.package === 'otro' && !payload) {
        return { success: false, needsPackageDimensions: true }
      }

      const variables = {
        packagingName: order.package,
        locationOrigin: 'bogota',
        departmentOrigin: 'bogota',
        locationDestination: order.location,
        departmentDestination: order.department,
        value: order.declared,
        isCod: false,
        packageDimensions: payload
      }

      const response = await gql.query(gqlCoordinadora.GET_QUOTE_COORDINADORA, variables, false, true, true)

      if (!response || response.error) {
        modals.showMessage({
          message: response && response.error ? response.error : 'Error al cotizar',
          type: 'error'
        })
        return { success: false }
      }

      commit('patchDataNoveltyFields', {
        order: order.order,
        patch: {
          coordinadoraCost: parseInt(response.data.getQuoteCoordinadora, 10),
          package: order.package,
          packageDimensions: payload || order.packageDimensions
        }
      })

      modals.showMessage({ message: 'Cotización exitosa' })
      return { success: true }
    },

    async generateDataNoveltyGuide ({ commit, dispatch, state }, { order, packageDimensions = null, collectFreight = false }) {
      if (!order) return { success: false }

      let current = state.dataOrders.find(item => item.order === order.order) || order

      if (!current.coordinadoraCost) {
        modals.showMessage({ message: 'Cotiza la orden antes de generar la guía', type: 'error' })
        return { success: false }
      }

      const dimensions = packageDimensions && current.package === 'otro' ? packageDimensions : null
      if (current.package === 'otro' && !dimensions && !current.packageDimensions) {
        return { success: false, needsPackageDimensions: true }
      }

      if (current.status !== 'packed' && current.status !== 'sent') {
        modals.showMessage({
          message: `La orden debe estar empacada o enviada para generar la guía (estado: ${current.status})`,
          type: 'error'
        })
        return { success: false }
      }

      if (needsNewDeliveryForDataNovelty(current)) {
        const errors = []
        await gql.mutate(
          gqlOrder.NEW_DELIVERY,
          { order: current.order, type: 'order' },
          false,
          errors
        )

        if (errors.length) {
          modals.showMessage({ message: errors[0], type: 'error' })
          return { success: false }
        }

        await dispatch('refreshDataNoveltyOrder', current.order)
        current = state.dataOrders.find(item => item.order === order.order) || current
      }

      const packageDimensionsPayload = dimensions || current.packageDimensions || null
      const errors = []
      const response = await gql.mutate(
        gqlCoordinadora.GENERATE_DELIVERY_COORDINADORA,
        {
          order: current.order,
          package: current.package,
          valueDeclared: current.declared,
          cost: current.coordinadoraCost,
          isChange: false,
          collectFreight,
          packageDimensions: current.package === 'otro' ? packageDimensionsPayload : null
        },
        false,
        errors
      )

      if (!response || errors.length) {
        modals.showMessage({ message: errors[0] || 'Error al generar la guía', type: 'error' })
        return { success: false }
      }

      const result = response.data.generateDeliveryCoordinadora
      const deliveries = (current.deliveries || []).slice()
      const latestIndex = deliveries.length - 1

      if (latestIndex >= 0) {
        deliveries[latestIndex] = {
          ...deliveries[latestIndex],
          idRemission: result.idRemission,
          tracking: result.tracking,
          conveyor: 'coordinadora',
          cost: result.cost,
          estimatedCost: result.estimatedCost,
          status: 'inprogress'
        }
      }

      commit('patchDataNoveltyFields', {
        order: current.order,
        patch: { deliveries }
      })

      modals.showMessage({
        message: collectFreight
          ? 'Guía al cobro generada'
          : 'Guía generada en Coordinadora'
      })

      return { success: true }
    },

    async closeDataNoveltyOrder ({ commit, dispatch }, orderId) {
      if (!orderId) return { success: false }

      const errors = []
      const response = await gql.mutate(
        gqlOrder.CLOSE_DATA_NOVELTY,
        { order: orderId },
        false,
        errors
      )

      if (!response || response.error || errors.length) {
        modals.showMessage({
          message: (response && response.error) || errors[0] || 'No se pudo cerrar la novedad',
          type: 'error'
        })
        return { success: false }
      }

      const result = response.data && response.data.closeOrderDataNovelty
      if (result) {
        commit('syncDataNoveltyFromSocket', result)
        dispatch('fetchDataNoveltyCount')
      }

      modals.showMessage({ message: 'Novedad de datos cerrada' })
      return { success: true }
    },

    async fetchProductChanges ({ commit }) {
      commit('setProductChangeLoading', true)

      try {
        const response = await gql.query(gqlOrder.GET_PRODUCT_CHANGES, {}, false, true, true)

        if (response && response.data) {
          commit('setProductChangeOrders', response.data.getOrderProductChanges || [])
        }
      } finally {
        commit('setProductChangeLoading', false)
      }
    },

    async fetchProductChangeDetail ({ commit }, orderId) {
      if (!orderId) {
        commit('setProductDetailOrder', null)
        return { success: false }
      }

      commit('setProductDetailLoading', true)

      try {
        const response = await gql.query(gqlOrder.GET_BY_ORDER_ADMIN, { order: orderId }, false, true, true)
        const found = response && response.data && response.data.getByOrderAdmin

        if (!response) {
          commit('setProductDetailOrder', null)
          return { success: false }
        }

        if (response.error) {
          commit('setProductDetailOrder', null)
          return { success: false, response }
        }

        if (!found) {
          commit('setProductDetailOrder', null)
          return { success: false, notFound: true }
        }

        if (!found.hasProductChange || !found.productChange) {
          commit('setProductDetailOrder', null)
          return { success: false, noChange: true }
        }

        commit('setProductDetailOrder', found)
        return { success: true, order: found }
      } finally {
        commit('setProductDetailLoading', false)
      }
    },

    refreshFromSocket ({ commit, dispatch, state }, dataUpdated) {
      if (!dataUpdated || !dataUpdated.order) return

      const orderId = dataUpdated.order
      const inDataList = state.dataOrders.some(item => item.order === orderId)
      const inProductList = state.productOrders.some(item => item.order === orderId)
      const inProductDetail = !!(state.productDetailOrder && state.productDetailOrder.order === orderId)

      const hasRemitDelta = dataUpdated.remitData !== undefined ||
        dataUpdated.remitDataGenerated !== undefined ||
        dataUpdated.dataLocked !== undefined

      if (hasRemitDelta && (inDataList || inProductList || inProductDetail)) {
        if (inDataList) commit('patchDataNoveltyRemitFromSocket', dataUpdated)
        if (inProductList || inProductDetail) commit('patchProductChangeRemitFromSocket', dataUpdated)
        return
      }

      if (dataUpdated.hasNoveltyData !== undefined || dataUpdated.noveltyData !== undefined) {
        commit('syncDataNoveltyFromSocket', dataUpdated)
        dispatch('fetchDataNoveltyCount')
      }

      if (dataUpdated.deliveries !== undefined || dataUpdated.status !== undefined) {
        if (inDataList) {
          commit('patchDataNoveltyFields', {
            order: orderId,
            patch: dataUpdated
          })
        }
      }

      if (dataUpdated.hasProductChange !== undefined || dataUpdated.productChange !== undefined) {
        commit('syncProductChangeFromSocket', dataUpdated)
        dispatch('fetchProductChangeCount')
      }

      if (hasRemitDelta && inDataList) {
        commit('patchDataNoveltyRemitFromSocket', dataUpdated)
      }
    },

    applyRemitUpdate ({ commit }, dataUpdated) {
      if (!dataUpdated) return

      if (dataUpdated.order) {
        commit('patchDataNoveltyRemitFromSocket', dataUpdated)
        commit('patchProductChangeRemitFromSocket', dataUpdated)
      }
    },

    async generateNoveltyRemit ({ commit }, { orderIds = [] }) {
      if (!orderIds.length) return

      for (const order of orderIds) {
        const errors = []
        const response = await gql.mutate(
          gqlOrder.GENERATE_REMIT_DATA,
          { order },
          false,
          errors
        )

        if (!response || response.error) {
          modals.showMessage({
            message: `${order}: ${formatRemitError((response && response.error) || errors[0])}`,
            type: 'error'
          })
          continue
        }

        const result = response.data && response.data.generateRemitData

        if (!result || !result.success) {
          modals.showMessage({
            message: `${order}: ${(result && result.message) || 'error al generar remite'}`,
            type: 'error'
          })
          continue
        }

        commit('patchDataNoveltyRemitFromSocket', result)
        commit('patchProductChangeRemitFromSocket', result)
      }

      commit('clearNoveltySelection')
    },

    persistDataNoveltyQuote (_, order) {
      persistNoveltyQuote(order)
    },

    setNoveltyActiveTab ({ commit }, tab) {
      commit('setNoveltyActiveTab', tab)
    },

    async rejectProductChange (_, { order, reason }) {
      const errors = []
      const response = await gql.mutate(
        gqlOrder.REJECT_PRODUCT_CHANGE,
        { order, reason },
        false,
        errors
      )

      if (!response || response.error) {
        modals.showMessage({
          message: (response && response.error && response.error.message) || errors[0] || 'No se pudo rechazar el cambio',
          type: 'error'
        })
        return false
      }

      modals.showMessage({ message: 'Cambio de producto rechazado' })
      return true
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/orderNovelty.js